import type {CpcCampaignModalState} from './types';

import {Button, Divider, Loading} from '@ds/components';
import {DetailPageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  cpcGet,
  deleteCPCCampaign,
  pauseCPCCampaign,
  renewalCPCCampaign
} from 'store/slices/cpc/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import CampaignActionModal from './components/CampaignActionModal/CampaignActionModal';
import CampaignOverview from './components/CampaignOverview/CampaignOverview';
import CampaignReport from './components/CampaignReport/CampaignReport';

const CpcDetails: React.FC = () => {
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const dispatch = useAppDispatch();
  const {cpcDetails, loading} = useAppSelector((state) => state.cpc);
  const [campaignModalState, setCampaignModalState] = useState<CpcCampaignModalState>();
  const [loadedCampaignId, setLoadedCampaignId] = useState<string>();
  const {id: campaignId} = useParams();

  const loadCampaign = useCallback(() => {
    if (!campaignId) {
      return Promise.resolve();
    }

    return dispatch(cpcGet({vendor_id: vendorId, id: campaignId}))
      .unwrap()
      .finally(() => setLoadedCampaignId(campaignId))
      .then(() => undefined);
  }, [campaignId, dispatch, vendorId]);

  useEffect(() => {
    void loadCampaign().catch(() => undefined);
  }, [loadCampaign]);

  const runCampaignAction = async (action: CpcCampaignModalState) => {
    if (!campaignId) {
      return;
    }

    try {
      if (action === 'remove') {
        await dispatch(deleteCPCCampaign({id: campaignId, vendor_id: vendorId})).unwrap();
      } else if (action === 'pause') {
        await dispatch(
          pauseCPCCampaign({
            id: campaignId,
            vendor_id: vendorId,
            hold: !cpcDetails?.hold
          })
        ).unwrap();
      } else {
        await dispatch(renewalCPCCampaign({id: campaignId, vendor_id: vendorId})).unwrap();
      }

      await loadCampaign();
    } catch (error) {
      const requestError = error as {data?: {errors?: {message?: string}}};
      toast(requestError.data?.errors?.message || 'خطا در انجام عملیات');
    }
  };

  if (loading || loadedCampaignId !== campaignId) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  if (!cpcDetails || String(cpcDetails.id) !== campaignId) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center px-4 text-center">
        <p className="text-body-sm text-gray-08">اطلاعات کمپین برای نمایش در دسترس نیست.</p>
      </div>
    );
  }

  const state = cpcDetails.state;

  return (
    <>
      <DetailPageLayout
        footer={
          state !== 'deleted' ? (
            <div className="flex gap-2.5">
              {state === 'initiated' ? (
                <Button
                  fullWidth
                  color="destructive"
                  onClick={() => setCampaignModalState('remove')}
                >
                  حذف کمپین
                </Button>
              ) : null}
              {state === 'active' ? (
                <>
                  <Button
                    fullWidth
                    color="destructive"
                    onClick={() => setCampaignModalState('renewal')}
                  >
                    توقف کامل کمپین
                  </Button>
                  <Button
                    fullWidth
                    color="secondary"
                    onClick={() => setCampaignModalState('pause')}
                  >
                    {cpcDetails.hold ? 'شروع دوباره' : 'توقف موقت'}
                  </Button>
                </>
              ) : null}
            </div>
          ) : undefined
        }
      >
        <CampaignOverview campaign={cpcDetails} />
        <Divider className="bg-gray-02" size={2} />
        <CampaignReport campaign={cpcDetails} />
      </DetailPageLayout>
      <CampaignActionModal
        action={campaignModalState}
        isPaused={Boolean(cpcDetails.hold)}
        onClose={() => setCampaignModalState(undefined)}
        onConfirm={(action) => {
          setCampaignModalState(undefined);
          void runCampaignAction(action);
        }}
      />
    </>
  );
};

export default CpcDetails;
